/**
 * DialPadView - PSTN/SIP keypad for making outbound calls
 */

'use client';

import React, { useEffect, useRef } from 'react';
import {
  Phone,
  Loader2,
  Delete,
  PhoneOff,
} from 'lucide-react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { isCapacitor } from '@/lib/platformUtils';
import { cn } from '@/lib/utils';
import type { DialPadSuggestion, DialPadViewProps } from '../types';
import { onlyDigits } from '../dialPadSuggestionUtils';

const ZERO_LONG_PRESS_MS = 380;

function HighlightedPhone({
  phone,
  queryRaw,
  highlightColor,
  mutedClass,
}: {
  phone: string;
  queryRaw: string;
  highlightColor: string;
  mutedClass: string;
}) {
  const q = onlyDigits(queryRaw);
  if (!q || q.length < 2) {
    return <span className={cn('font-mono text-sm', mutedClass)}>{phone}</span>;
  }
  const pNorm = onlyDigits(phone);
  const idx = pNorm.indexOf(q);
  if (idx < 0) {
    return <span className={cn('font-mono text-sm', mutedClass)}>{phone}</span>;
  }

  let digitIdx = 0;
  let startChar = -1;
  let endChar = -1;
  for (let i = 0; i < phone.length; i++) {
    if (/\d/.test(phone[i])) {
      if (digitIdx === idx) startChar = i;
      if (digitIdx === idx + q.length - 1) {
        endChar = i;
        break;
      }
      digitIdx++;
    }
  }
  if (startChar < 0 || endChar < 0) {
    return <span className={cn('font-mono text-sm', mutedClass)}>{phone}</span>;
  }

  const before = phone.slice(0, startChar);
  const match = phone.slice(startChar, endChar + 1);
  const after = phone.slice(endChar + 1);
  return (
    <span className={cn('font-mono text-sm', mutedClass)}>
      {before}
      <span style={{ color: highlightColor }} className="font-semibold">
        {match}
      </span>
      {after}
    </span>
  );
}

function sourceLabel(source: DialPadSuggestion['source']): string {
  switch (source) {
    case 'crm':
      return 'CRM';
    case 'recent':
      return 'Recent';
    case 'team':
      return 'Team';
    default:
      return '';
  }
}

export function DialPadView({
  extension,
  dialNumber,
  onDialNumberChange,
  onDial,
  isDialing,
  onCancelOutboundDial,
  isDark,
  primaryColor,
  inCall = false,
  onSendDtmf,
  dialSuggestions = [],
}: DialPadViewProps) {
  const dialNumberRef = useRef(dialNumber);
  dialNumberRef.current = dialNumber;

  const doHaptic = async (style: ImpactStyle = ImpactStyle.Light) => {
    if (isCapacitor()) {
      try {
        await Haptics.impact({ style });
      } catch {
        // Ignore haptic errors
      }
    }
  };

  const handleDial = () => {
    if (!dialNumber || isDialing) return;
    doHaptic(ImpactStyle.Heavy);
    onDial(dialNumber);
  };

  const handleMainCallButton = () => {
    if (isDialing && onCancelOutboundDial) {
      doHaptic(ImpactStyle.Medium);
      onCancelOutboundDial();
      return;
    }
    handleDial();
  };

  const showCancelOutbound = isDialing && !!onCancelOutboundDial;

  const handleDigitPress = (digit: string) => {
    if (inCall && onSendDtmf) {
      onSendDtmf(digit);
      onDialNumberChange(dialNumber + digit);
    } else {
      onDialNumberChange(dialNumber + digit);
    }
    doHaptic(ImpactStyle.Light);
  };

  const handleBackspace = () => {
    if (dialNumber.length > 0) {
      onDialNumberChange(dialNumber.slice(0, -1));
      doHaptic(ImpactStyle.Light);
    }
  };

  const zeroLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zeroDidLongPressRef = useRef(false);

  const clearZeroLongPressTimer = () => {
    if (zeroLongPressTimerRef.current) {
      clearTimeout(zeroLongPressTimerRef.current);
      zeroLongPressTimerRef.current = null;
    }
  };

  useEffect(() => () => clearZeroLongPressTimer(), []);

  const onZeroPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    zeroDidLongPressRef.current = false;
    clearZeroLongPressTimer();
    zeroLongPressTimerRef.current = setTimeout(() => {
      zeroLongPressTimerRef.current = null;
      zeroDidLongPressRef.current = true;
      onDialNumberChange(`${dialNumberRef.current}+`);
      doHaptic(ImpactStyle.Medium);
    }, ZERO_LONG_PRESS_MS);
  };

  const onZeroPointerUp = () => {
    clearZeroLongPressTimer();
    if (zeroDidLongPressRef.current) {
      zeroDidLongPressRef.current = false;
      return;
    }
    handleDigitPress('0');
  };

  const onZeroPointerCancel = () => {
    clearZeroLongPressTimer();
    zeroDidLongPressRef.current = false;
  };

  const digits = [
    { num: '1', letters: '' },
    { num: '2', letters: 'ABC' },
    { num: '3', letters: 'DEF' },
    { num: '4', letters: 'GHI' },
    { num: '5', letters: 'JKL' },
    { num: '6', letters: 'MNO' },
    { num: '7', letters: 'PQRS' },
    { num: '8', letters: 'TUV' },
    { num: '9', letters: 'WXYZ' },
    { num: '*', letters: '' },
    { num: '0', letters: '+' },
    { num: '#', letters: '' },
  ];

  const suggestionMuted = isDark ? 'text-gray-300' : 'text-gray-700';

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 sm:px-8 pt-6 pb-2 sm:pt-8 sm:pb-6">
        <div className={`text-center mb-2 sm:mb-3 text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {extension?.trunk_name ? (
            <div
              className={`mb-1 px-2 text-[11px] font-medium leading-tight sm:text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}
              title="SIP trunk"
            >
              {extension.trunk_name}
            </div>
          ) : null}
          {extension ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Ext. {extension.extension_number} • {extension.display_name || 'Ready'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              Internal dialing ready • External calls require a SIP extension
            </span>
          )}
        </div>

        {/* Number input + predictive suggestions — the dropdown overlays upward over the
            extension section instead of pushing the keypad down. */}
        <div className="relative flex w-full flex-col items-center gap-2 px-1">
          {dialSuggestions.length > 0 ? (
            <div
              className={`absolute bottom-full left-1/2 z-10 mb-2 w-full max-w-md -translate-x-1/2 overflow-y-auto rounded-xl border shadow-xl max-h-36 sm:max-h-44 ${
                isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              <ul className="divide-y divide-gray-700/20 dark:divide-gray-600/30">
                {dialSuggestions.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onDialNumberChange(s.dialString);
                        doHaptic(ImpactStyle.Light);
                      }}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors sm:px-4',
                        isDark ? 'hover:bg-gray-700/50 active:bg-gray-700' : 'hover:bg-gray-100 active:bg-gray-50',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white',
                          s.source === 'crm'
                            ? 'bg-blue-600'
                            : s.source === 'recent'
                              ? 'bg-amber-600'
                              : 'bg-violet-600',
                        )}
                      >
                        {s.displayName.charAt(0).toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              'truncate text-sm font-medium',
                              isDark ? 'text-white' : 'text-gray-900',
                            )}
                          >
                            {s.displayName}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                              isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600',
                            )}
                          >
                            {sourceLabel(s.source)}
                          </span>
                        </div>
                        <HighlightedPhone
                          phone={s.phoneDisplay}
                          queryRaw={dialNumber}
                          highlightColor={primaryColor}
                          mutedClass={suggestionMuted}
                        />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <input
            type="tel"
            inputMode="tel"
            value={dialNumber}
            onChange={(e) => onDialNumberChange(e.target.value)}
            className={`min-h-[40px] w-full max-w-md text-center font-mono text-2xl font-light tracking-wider outline-none sm:min-h-[64px] sm:text-5xl ${isDark ? 'text-white' : 'text-gray-900'}`}
            style={{
              caretColor: primaryColor,
              // Override the global input[type="tel"] theming — the dial field
              // must be chromeless (no border/background) in both themes.
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              padding: 0,
              borderRadius: 0,
            }}
          />
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[280px] flex-1 flex-col justify-center px-6 py-1 sm:max-w-sm sm:px-8 sm:py-6">
        <div className="grid grid-cols-3 gap-1.5 touch-manipulation sm:gap-4">
          {digits.map(({ num, letters }) =>
            num === '0' ? (
              <button
                key={num}
                type="button"
                onPointerDown={onZeroPointerDown}
                onPointerUp={onZeroPointerUp}
                onPointerCancel={onZeroPointerCancel}
                onPointerLeave={onZeroPointerCancel}
                onContextMenu={(e) => e.preventDefault()}
                className={cn(
                  'mx-auto flex h-[64px] w-[64px] cursor-pointer select-none flex-col items-center justify-center rounded-full transition-all duration-150 active:scale-95 sm:aspect-square sm:h-auto sm:w-auto',
                  isDark
                    ? 'bg-gray-800 ring-1 ring-gray-700/50 hover:bg-gray-700 hover:ring-gray-600 active:bg-gray-600'
                    : 'bg-white shadow-md ring-1 ring-gray-200 hover:bg-gray-50 hover:shadow-lg active:bg-gray-100',
                )}
              >
                <span className={`text-xl font-semibold sm:text-3xl ${isDark ? 'text-white' : 'text-gray-900'}`}>{num}</span>
                {letters ? (
                  <span
                    className={`text-[8px] font-medium tracking-widest sm:text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                  >
                    {letters}
                  </span>
                ) : null}
              </button>
            ) : (
              <button
                key={num}
                type="button"
                onClick={() => handleDigitPress(num)}
                className={cn(
                  'mx-auto flex h-[64px] w-[64px] cursor-pointer select-none flex-col items-center justify-center rounded-full transition-all duration-150 active:scale-95 sm:aspect-square sm:h-auto sm:w-auto',
                  isDark
                    ? 'bg-gray-800 ring-1 ring-gray-700/50 hover:bg-gray-700 hover:ring-gray-600 active:bg-gray-600'
                    : 'bg-white shadow-md ring-1 ring-gray-200 hover:bg-gray-50 hover:shadow-lg active:bg-gray-100',
                )}
              >
                <span className={`text-xl font-semibold sm:text-3xl ${isDark ? 'text-white' : 'text-gray-900'}`}>{num}</span>
                {letters ? (
                  <span
                    className={`text-[8px] font-medium tracking-widest sm:text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                  >
                    {letters}
                  </span>
                ) : null}
              </button>
            ),
          )}
        </div>

        <div className="relative mt-3 flex items-center justify-center gap-3 sm:mt-8 sm:gap-6">
          {/* Fixed-width slots keep the dial button centred whether or not backspace shows */}
          <div className="w-12 shrink-0 sm:w-16" aria-hidden />

          <button
            type="button"
            onClick={handleMainCallButton}
            title={showCancelOutbound ? 'Cancel outgoing call' : undefined}
            disabled={
              showCancelOutbound
                ? false
                : !dialNumber || (isDialing && !onCancelOutboundDial)
            }
            className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full shadow-lg transition-all duration-200 active:scale-95 sm:h-20 sm:w-20 ${
              showCancelOutbound
                ? 'bg-red-500 text-white hover:bg-red-600 hover:shadow-xl'
                : dialNumber && !isDialing
                  ? 'bg-green-500 text-white hover:opacity-90 hover:shadow-xl'
                  : isDark
                    ? 'cursor-not-allowed bg-gray-800 text-gray-500 ring-1 ring-gray-700'
                    : 'cursor-not-allowed bg-gray-200 text-gray-400'
            }`}
            style={
              showCancelOutbound
                ? { boxShadow: '0 8px 25px -5px rgba(239, 68, 68, 0.45)' }
                : dialNumber && !isDialing
                  ? {
                      backgroundColor: '#22c55e',
                      boxShadow: '0 8px 25px -5px rgba(34, 197, 94, 0.4)',
                    }
                  : {}
            }
          >
            {showCancelOutbound ? (
              <PhoneOff className="h-7 w-7 sm:h-9 sm:w-9" />
            ) : isDialing ? (
              <Loader2 className="h-7 w-7 animate-spin sm:h-9 sm:w-9" />
            ) : (
              <Phone className="h-7 w-7 sm:h-9 sm:w-9" />
            )}
          </button>

          <div className="flex w-12 shrink-0 items-center justify-center sm:w-16">
            {dialNumber ? (
              <button
                type="button"
                onClick={handleBackspace}
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-150 active:scale-95 sm:h-16 sm:w-16 ${
                  isDark
                    ? 'bg-gray-800 text-gray-300 ring-1 ring-gray-700 hover:bg-gray-700'
                    : 'bg-white text-gray-600 shadow-lg ring-1 ring-gray-200 hover:bg-gray-50'
                }`}
                title="Backspace"
                aria-label="Backspace"
              >
                <Delete className="h-5 w-5 sm:h-7 sm:w-7" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className={`hidden border-t px-4 py-3 text-center sm:block sm:px-6 sm:py-4 ${
          isDark ? 'border-gray-800 bg-gray-900/50' : 'border-gray-100 bg-gray-50/50'
        }`}
      >
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {extension
            ? `Calls via ${extension.trunk_name || "your organization's SIP trunk"}`
            : 'Dial a teammate by internal extension, or use contacts and recents to start an internal call'}
        </p>
        <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>
          {extension ? 'Standard call rates may apply' : 'PSTN / external dialing unlocks automatically once a SIP extension is assigned'}
        </p>
      </div>
    </div>
  );
}

export default DialPadView;
